import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, FileText, ShoppingBag, CreditCard, Trash2, Shield, DollarSign, TrendingUp, Clock, CheckCircle, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [updatingTier, setUpdatingTier] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberStats, setMemberStats] = useState(null);
  const [activatingUser, setActivatingUser] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      // Load critical data first (stats + subscriptions), then rest in parallel
      const [statsRes, subsRes] = await Promise.all([
        api.get('/admin/stats').catch(() => ({ data: null })),
        api.get('/admin/subscriptions').catch(() => ({ data: { users: [] } })),
      ]);
      setStats(statsRes.data);

      const allMembers = subsRes.data?.users || [];
      setMembers(allMembers);
      const active = allMembers.filter(m => m.subscription_status === 'active').length;
      const trial = allMembers.filter(m => m.subscription_status === 'trial').length;
      const expired = allMembers.filter(m => m.subscription_status === 'expired').length;
      const recentNew = allMembers.filter(m => {
        if (!m.created_at) return false;
        return (new Date() - new Date(m.created_at)) < 30 * 24 * 60 * 60 * 1000;
      }).length;
      setMemberStats({ active, trial, expired, total: allMembers.length, recentNew });
    } catch {} finally {
      setLoading(false);
    }

    // Load remaining data in background (non-blocking)
    Promise.allSettled([
      api.get('/admin/users'),
      api.get('/admin/posts'),
      api.get('/admin/sellers'),
      api.get('/admin/payouts'),
      api.get('/admin/transactions'),
      api.get('/admin/commission-tiers'),
    ]).then(results => {
      const val = (i, fb) => results[i]?.status === 'fulfilled' ? results[i].value.data : fb;
      setUsers(val(0, []));
      setPosts(val(1, []));
      setSellers(val(2, []));
      setPayouts(val(3, []));
      setTransactions(val(4, []));
      setCommissionTiers(val(5, { tiers: [] })?.tiers || []);
    });
  };

  const deleteUser = async (uid) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.delete(`/admin/users/${uid}`); setUsers(p => p.filter(u => u.id !== uid)); toast.success('User deleted'); }
    catch { toast.error('Failed'); }
  };

  const deletePost = async (pid) => {
    try { await api.delete(`/admin/posts/${pid}`); setPosts(p => p.filter(x => x.id !== pid)); toast.success('Post deleted'); }
    catch { toast.error('Failed'); }
  };

  const toggleAdmin = async (uid, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    try { await api.put(`/admin/users/${uid}/role?role=${newRole}`); loadData(); toast.success('Role updated'); }
    catch { toast.error('Failed'); }
  };

  const processPayout = async (payoutId) => {
    try {
      await api.post(`/admin/payouts/${payoutId}/process`);
      toast.success('Payout processed');
      loadData();
    } catch { toast.error('Failed to process payout'); }
  };

  const updateCommissionTier = async (userId, newTier) => {
    setUpdatingTier(userId);
    try {
      await api.put(`/admin/creators/${userId}/tier`, { tier: newTier });
      toast.success('Commission tier updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update commission tier');
    } finally {
      setUpdatingTier(null);
    }
  };

  const activateSubscription = async (userId, plan) => {
    setActivatingUser(userId);
    try {
      await api.post('/admin/subscription/activate', { user_id: userId, plan });
      toast.success('Subscription activated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate');
    } finally {
      setActivatingUser(null);
    }
  };

  const deactivateSubscription = async (userId) => {
    if (!window.confirm('Deactivate this user\'s subscription?')) return;
    setActivatingUser(userId);
    try {
      await api.post(`/admin/subscription/deactivate/${userId}`);
      toast.success('Subscription deactivated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    } finally {
      setActivatingUser(null);
    }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground text-sm">Loading...</div>;

  const platformCards = [
    { label: 'Users', value: stats?.total_users || 0, icon: Users },
    { label: 'Products', value: stats?.total_products || 0, icon: ShoppingBag },
    { label: 'Revenue', value: `${(stats?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, gold: true },
    { label: 'Commission', value: `${(stats?.total_commission_earned || 0).toFixed(2)}`, icon: TrendingUp, gold: true },
    { label: 'Pending Payouts', value: stats?.pending_payouts_count || 0, icon: Clock },
  ];

  return (
    <div className="animate-in" data-testid="admin-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-8">Admin Dashboard</h1>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
        {platformCards.map((s, i) => (
          <div key={i} className="border border-border/40 p-4 hover:border-gold/30 transition-colors animate-in" style={{ animationDelay: `${i * 60}ms` }} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <s.icon size={16} className={s.gold ? 'text-gold mb-2' : 'text-foreground/50 mb-2'} />
            <p className={`font-serif text-2xl ${s.gold ? 'text-gold' : ''}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="memberships" className="w-full">
        <TabsList className="rounded-sm bg-mist h-10 mb-6">
          <TabsTrigger value="memberships" className="rounded-sm text-xs uppercase tracking-wider" data-testid="admin-tab-memberships">Memberships</TabsTrigger>
          <TabsTrigger value="payouts" className="rounded-sm text-xs uppercase tracking-wider" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="sellers" className="rounded-sm text-xs uppercase tracking-wider" data-testid="admin-tab-sellers">Sellers</TabsTrigger>
          <TabsTrigger value="sales" className="rounded-sm text-xs uppercase tracking-wider" data-testid="admin-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm text-xs uppercase tracking-wider" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="posts" className="rounded-sm text-xs uppercase tracking-wider" data-testid="admin-tab-posts">Posts</TabsTrigger>
        </TabsList>

        {/* Memberships Tab */}
        <TabsContent value="memberships" data-testid="admin-memberships-tab">
          {/* Membership Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Active Members', value: memberStats?.active || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', sub: '\u20AC29/mo each' },
              { label: 'Free Trial', value: memberStats?.trial || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', sub: '30-day trial' },
              { label: 'Expired', value: memberStats?.expired || 0, icon: Users, color: 'text-red-600', bg: 'bg-red-50', sub: 'Paused access' },
              { label: 'New (30d)', value: memberStats?.recentNew || 0, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', sub: 'Recent signups' },
              { label: 'Total Users', value: memberStats?.total || 0, icon: Users, color: 'text-obsidian', bg: 'bg-stone-50', sub: 'All accounts' },
            ].map((s, i) => (
              <div key={i} className={`${s.bg} border border-border/30 rounded-xl p-4`} data-testid={`member-stat-${s.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
                <s.icon size={16} className={`${s.color} mb-2`} />
                <p className={`font-serif text-2xl ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Monthly Revenue Estimate */}
          <div className="border border-amber-200/60 rounded-xl p-4 mb-6 bg-gradient-to-r from-amber-50/50 to-white" data-testid="mrr-estimate">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Creator Members MRR</p>
                <p className="font-serif text-xl text-amber-700">{'\u20AC'}{((memberStats?.active || 0) * 29).toLocaleString()}<span className="text-xs text-muted-foreground font-sans">/month</span></p>
                <p className="text-[10px] text-muted-foreground">{memberStats?.active || 0} members {'\u00D7'} {'\u20AC'}29/mo</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Advanced AI Studio MRR</p>
                <p className="font-serif text-xl text-violet-700">{'\u20AC'}0<span className="text-xs text-muted-foreground font-sans">/month</span></p>
                <p className="text-[10px] text-muted-foreground">Coming soon — {'\u20AC'}49/mo tier</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Platform MRR</p>
                <p className="font-serif text-xl text-emerald-700">{'\u20AC'}{((memberStats?.active || 0) * 29).toLocaleString()}<span className="text-xs text-muted-foreground font-sans">/month</span></p>
                <p className="text-[10px] text-muted-foreground">All subscription revenue</p>
              </div>
            </div>
          </div>

          {/* Members Table */}
          <div className="border border-border/40 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-wider">User</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Plan</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Expires / Trial End</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Joined</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.filter(m => m.role !== 'admin').map(m => {
                  const status = m.subscription_status || 'unknown';
                  const endDate = m.subscription_end_date || m.trial_end_date || '';
                  const statusColors = {
                    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                    trial: 'bg-amber-100 text-amber-700 border-amber-200',
                    expired: 'bg-red-100 text-red-700 border-red-200',
                  };
                  return (
                    <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                      <TableCell className="text-xs font-medium">{m.display_name || m.username}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.email}</TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] capitalize rounded-full ${statusColors[status] || 'bg-stone-100 text-stone-600'}`}>
                          {status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{m.subscription_plan === 'advanced' ? `Advanced \u20AC49/mo` : m.subscription_plan ? `Creator \u20AC29/mo` : '—'}</TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">{endDate ? new Date(endDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="text-right">
                        {status !== 'active' ? (
                          <Button
                            size="sm"
                            onClick={() => activateSubscription(m.id, 'monthly')}
                            disabled={activatingUser === m.id}
                            className="text-[10px] h-7 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`activate-${m.id}`}
                          >
                            {activatingUser === m.id ? <Loader2 size={10} className="animate-spin" /> : 'Activate'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deactivateSubscription(m.id)}
                            disabled={activatingUser === m.id}
                            className="text-[10px] h-7 px-3 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`deactivate-${m.id}`}
                          >
                            {activatingUser === m.id ? <Loader2 size={10} className="animate-spin" /> : 'Deactivate'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Payouts Tab */}
        <TabsContent value="payouts">
          <div className="border border-border/40 overflow-hidden">
            <div className="p-4 border-b border-border/20 bg-mist/30 flex justify-between items-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending payout amount: <span className="text-gold font-serif">{(stats?.pending_payouts_amount || 0).toFixed(2)}</span></p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-mist">
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Seller</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium w-24">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No payout requests</TableCell></TableRow>
                ) : payouts.map(p => (
                  <TableRow key={p.id} data-testid={`admin-payout-${p.id}`}>
                    <TableCell className="text-sm">{p.seller_name || p.seller_id}</TableCell>
                    <TableCell className="text-sm font-serif text-gold">{p.amount?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm text-[10px] uppercase ${p.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                        {p.status === 'completed' ? <CheckCircle size={10} className="mr-1" /> : <Clock size={10} className="mr-1" />}
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {p.status === 'pending' && (
                        <Button size="sm" className="h-7 text-[10px] rounded-sm bg-gold text-obsidian hover:bg-gold/80" onClick={() => processPayout(p.id)} data-testid={`process-payout-${p.id}`}>
                          Process
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Sellers Tab */}
        <TabsContent value="sellers">
          <div className="border border-border/40 overflow-hidden">
            {/* Commission Tier Legend */}
            <div className="p-4 border-b border-border/20 bg-gradient-to-r from-gold/5 to-purple-500/5">
              <div className="flex items-center gap-2 mb-3">
                <Crown size={16} className="text-gold" />
                <span className="text-xs font-medium uppercase tracking-wider">Commission Tiers</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {commissionTiers.map(tier => (
                  <div key={tier.id} className="flex items-center gap-2 text-xs">
                    <Badge className={`rounded-sm text-[10px] ${
                      tier.id === 'starter_creator' ? 'bg-blue-100 text-blue-700' :
                      tier.id === 'growth_creator' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {(tier.rate * 100).toFixed(0)}%
                    </Badge>
                    <span className="text-muted-foreground">{tier.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-mist">
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Seller</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Sales</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Earnings</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Commission Paid</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Commission Tier</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Pending</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No sellers yet</TableCell></TableRow>
                ) : sellers.map(s => (
                  <TableRow key={s.user_id} data-testid={`admin-seller-${s.user_id}`}>
                    <TableCell className="text-sm font-medium">{s.display_name || s.user_id}</TableCell>
                    <TableCell className="text-sm">{s.sales_count}</TableCell>
                    <TableCell className="text-sm font-serif text-gold">€{(s.total_earnings || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm">€{(s.total_commission || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {updatingTier === s.user_id ? (
                          <Loader2 size={14} className="animate-spin text-gold" />
                        ) : (
                          <Select
                            value={s.commission_tier || 'starter_creator'}
                            onValueChange={(value) => updateCommissionTier(s.user_id, value)}
                          >
                            <SelectTrigger className="w-[160px] h-8 text-xs rounded-sm" data-testid={`tier-select-${s.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {commissionTiers.map(tier => (
                                <SelectItem key={tier.id} value={tier.id} className="text-xs">
                                  <span className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${
                                      tier.id === 'starter_creator' ? 'bg-blue-500' :
                                      tier.id === 'growth_creator' ? 'bg-green-500' :
                                      'bg-purple-500'
                                    }`}></span>
                                    {tier.name} ({(tier.rate * 100).toFixed(0)}%)
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">€{(s.pending_balance || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales">
          <div className="border border-border/40 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-mist">
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Product</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Commission</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Seller Gets</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No sales yet</TableCell></TableRow>
                ) : transactions.map(t => (
                  <TableRow key={t.id} data-testid={`admin-txn-${t.id}`}>
                    <TableCell className="text-sm font-medium">{t.product_title}</TableCell>
                    <TableCell className="text-sm">{(t.amount || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-gold">{(t.commission_amount || 0).toFixed(2)} ({((t.commission_rate || 0) * 100).toFixed(0)}%)</TableCell>
                    <TableCell className="text-sm">{(t.seller_earnings || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="border border-border/40 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-mist">
                  <TableHead className="text-xs uppercase tracking-wider font-medium">User</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Joined</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                    <TableCell className="text-sm font-medium">{u.display_name || u.username}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm text-[10px] uppercase ${u.role === 'admin' ? 'bg-gold text-obsidian' : 'bg-mist text-foreground'}`}>{u.role}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleAdmin(u.id, u.role)} data-testid={`toggle-role-${u.id}`}>
                          <Shield size={12} className={u.role === 'admin' ? 'text-gold' : ''} />
                        </Button>
                        {u.id !== user?.id && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Posts Tab */}
        <TabsContent value="posts">
          <div className="border border-border/40 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-mist">
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Author</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Content</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium w-16">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map(p => (
                  <TableRow key={p.id} data-testid={`admin-post-${p.id}`}>
                    <TableCell className="text-sm font-medium">{p.author_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{p.content}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => deletePost(p.id)} data-testid={`admin-delete-post-${p.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
